import {
	DesignSystemPrimitive,
	PRIMITIVE_CATEGORY_COMMENT_MAP,
	PrimitiveCategory,
	CSSProperty,
	MediaQuery,
	CSSMediaQuery,
	CSSPseudo,
	CSSMediaQueryPseudo,
} from '../types';

// Normalize section comment text to category type using formal mapping
function normalizeCategory(comment: string): PrimitiveCategory {
	const normalized = comment.trim();
	// Look up in formal mapping (case-insensitive)
	const category = Object.entries(PRIMITIVE_CATEGORY_COMMENT_MAP).find(([key]) => {
		const keyLower = key.toLowerCase();
		const normalizedLower = normalized.toLowerCase();
		return normalizedLower.includes(keyLower) || keyLower.includes(normalizedLower);
	})?.[1];

	if (category) {
		return category;
	}

	// Fallback: assign to 'other' if not found
	return 'other';
}

// Generate display-friendly name from CSS class name
function generateDisplayName(className: string): string {
	// Remove leading dot and any pseudo-selectors
	const withoutDot = className.replace(/^\./, '').split(':')[0].split('[')[0];
	// Split by hyphens and capitalize each word
	const words = withoutDot.split('-').map((word) => {
		return word.charAt(0).toUpperCase() + word.slice(1);
	});
	return words.join(' ');
}

// Extract base selector from full selector (e.g., ".app-shell:hover" -> ".app-shell")
function extractBaseSelector(fullSelector: string): string {
	// Remove pseudo-classes and pseudo-elements
	const withoutPseudo = fullSelector.split(':')[0].split('::')[0];
	// Extract first part before space (for nested selectors)
	const parts = withoutPseudo.trim().split(/\s+/);
	return parts[0];
}

// Extract pseudo-class from selector (e.g., ".app-shell:hover" -> "hover")
function extractPseudoClass(fullSelector: string): string | undefined {
	const pseudoMatch = fullSelector.match(/:([a-zA-Z-]+)(?:\(|$)/);
	return pseudoMatch ? pseudoMatch[1] : undefined;
}

// Check if selector is nested (contains descendant selector)
function isNestedSelector(fullSelector: string): boolean {
	const baseSelector = extractBaseSelector(fullSelector);
	return fullSelector.trim() !== baseSelector && fullSelector.includes(' ');
}

// Parse media query condition
function parseMediaQuery(condition: string): MediaQuery {
	return { condition: condition.trim() };
}

// Parse CSS properties from a CSS rule block
function parseProperties(cssBlock: string): CSSProperty[] {
	const properties: CSSProperty[] = [];

	// Remove comments from the block
	const withoutComments = cssBlock.replace(/\/\*[\s\S]*?\*\//g, '');

	// Match property: value pairs
	const propertyRegex = /([a-zA-Z-]+)\s*:\s*([^;]+);/g;
	let match;

	while ((match = propertyRegex.exec(withoutComments)) !== null) {
		const name = match[1].trim();
		const value = match[2].trim();
		properties.push({ name, value });
	}

	return properties;
}

// Extract description from comments before a CSS rule
function extractDescription(lines: string[], startIndex: number): string | undefined {
	let description: string | undefined;
	let i = startIndex - 1;

	// Look backwards for comments
	while (i >= 0 && i >= startIndex - 3) {
		const line = lines[i].trim();
		if (line.startsWith('/*') && line.endsWith('*/')) {
			const commentText = line.replace(/\/\*\s*|\s*\*\//g, '').trim();
			// Skip section comments (they have dashes)
			if (!commentText.includes('---') && commentText.length > 0) {
				description = commentText;
				break;
			}
		}
		i--;
	}

	return description;
}

// Internal structure for parsing
interface ParsedRule {
	fullSelector: string;
	properties: CSSProperty[];
	mediaQueryStack: MediaQuery[]; // Stack of nested media queries
	pseudoClass?: string;
	rawCSS: string; // Original CSS block for this rule
	lineStart: number;
	lineEnd: number;
	category: PrimitiveCategory; // Category at time of parsing
	subcategory?: string; // Subcategory at time of parsing
}

interface ParsedPrimitiveData {
	baseSelector: string;
	category: PrimitiveCategory;
	subcategory?: string;
	description?: string;
	baseRule?: ParsedRule; // Rule with no media query, no pseudo-class
	allRules: ParsedRule[]; // All rules for fullCSS generation
}

// Generate fullCSS from all rules
function generateFullCSS(allRules: ParsedRule[]): string {
	if (allRules.length === 0) {
		return '';
	}

	// Sort rules: base first, then by line number
	const sortedRules = [...allRules].sort((a, b) => {
		// Base rule (no media query, no pseudo-class) comes first
		if (
			!a.mediaQueryStack.length &&
			!a.pseudoClass &&
			(b.mediaQueryStack.length || b.pseudoClass)
		) {
			return -1;
		}
		if (
			(a.mediaQueryStack.length || a.pseudoClass) &&
			!b.mediaQueryStack.length &&
			!b.pseudoClass
		) {
			return 1;
		}
		return a.lineStart - b.lineStart;
	});

	// Group rules by media query stack
	const rulesByMedia: Map<string, ParsedRule[]> = new Map();
	const baseRules: ParsedRule[] = [];

	for (const rule of sortedRules) {
		if (rule.mediaQueryStack.length === 0) {
			baseRules.push(rule);
		} else {
			const mediaKey = rule.mediaQueryStack.map((mq) => mq.condition).join(' AND ');
			if (!rulesByMedia.has(mediaKey)) {
				rulesByMedia.set(mediaKey, []);
			}
			rulesByMedia.get(mediaKey)!.push(rule);
		}
	}

	// Build CSS string
	const cssParts: string[] = [];

	// Add base rules
	for (const rule of baseRules) {
		cssParts.push(rule.rawCSS);
	}

	// Add media query blocks
	for (const [, rules] of rulesByMedia.entries()) {
		const stackLength = rules[0].mediaQueryStack.length;

		// Build nested media queries from outermost to innermost
		let mediaQueryStart = '';
		for (let i = 0; i < stackLength; i++) {
			const mq = rules[0].mediaQueryStack[i];
			const indent = '  '.repeat(i);
			if (i === 0) {
				mediaQueryStart = `${indent}@media ${mq.condition} {`;
			} else {
				mediaQueryStart += `\n${indent}@media ${mq.condition} {`;
			}
		}

		// Extract rules content (without @media wrapper)
		const rulesContent = rules
			.map((rule) => {
				// Extract just the selector and properties from rawCSS
				const ruleLines = rule.rawCSS.split('\n');
				return ruleLines
					.map((line) => line.trim())
					.filter((line) => line.length > 0)
					.join('\n');
			})
			.join('\n\n');

		// Close media queries from innermost to outermost
		// Indent rules content based on nesting depth
		const rulesIndent = '  '.repeat(stackLength);
		let mediaQueryEnd = '';
		for (let i = stackLength - 1; i >= 0; i--) {
			const indent = '  '.repeat(i);
			mediaQueryEnd += `\n${indent}}`;
		}

		cssParts.push(`${mediaQueryStart}\n${rulesIndent}${rulesContent}${mediaQueryEnd}`);
	}

	return cssParts.join('\n\n');
}

export function parsePrimitives(cssString: string): DesignSystemPrimitive[] {
	const lines = cssString.split('\n');
	const parsedRules: ParsedRule[] = [];
	let currentCategory: PrimitiveCategory = 'other';
	let currentSubcategory: string | undefined;
	const mediaQueryStack: MediaQuery[] = [];

	// Extract section comments (main categories)
	// Only accepts format with dashes: /* --- Layout primitives --- */
	const sectionCommentRegex = /\/\*\s*-+\s*([^-]+?)\s*-+\s*\*\//gi;

	let i = 0;
	while (i < lines.length) {
		const line = lines[i];
		const trimmed = line.trim();

		// Check for section comment with dashes (required format)
		const sectionMatch = trimmed.match(sectionCommentRegex);
		if (sectionMatch) {
			const categoryText = sectionMatch[0]
				.replace(/\/\*\s*-+\s*/, '')
				.replace(/\s*-+\s*\*\//, '')
				.trim();
			currentCategory = normalizeCategory(categoryText);
			currentSubcategory = undefined;
			i++;
			continue;
		}

		// Check for inline subcategory comment (no dashes)
		if (trimmed.startsWith('/*') && trimmed.endsWith('*/')) {
			const subcategoryText = trimmed.replace(/\/\*\s*|\s*\*\//g, '').trim();
			// Skip section comments (already handled above - they have dashes)
			if (!subcategoryText.includes('---')) {
				currentSubcategory = subcategoryText;
			}
			i++;
			continue;
		}

		// Check for @media query start
		const mediaMatch = trimmed.match(/@media\s+(.+?)\s*\{/);
		if (mediaMatch) {
			const condition = mediaMatch[1].trim();
			mediaQueryStack.push(parseMediaQuery(condition));
			i++;
			continue;
		}

		// Check for closing brace (could be end of media query or rule)
		if (trimmed === '}') {
			if (mediaQueryStack.length > 0) {
				mediaQueryStack.pop();
			}
			i++;
			continue;
		}

		// Extract CSS class selector (e.g., .btn-primary, .card, ds-card, .app-shell:hover, .app-shell .sidebar)
		// Match selectors that start with . or are custom elements (ds-*)
		// Also handle pseudo-classes and nested selectors
		const classSelectorRegex =
			/^(\.?[a-zA-Z0-9_-]+|ds-[a-zA-Z0-9_-]+)(?:\s*[,:]\s*|(?:\s*\{)|(?:\s+[\.ds-]))/;
		const selectorMatch = trimmed.match(classSelectorRegex);

		if (selectorMatch && trimmed.includes('{')) {
			// Extract full selector (may include pseudo-classes and nested parts)
			const fullSelectorMatch = trimmed.match(/^([^{]+)\{/);
			if (fullSelectorMatch) {
				const fullSelector = fullSelectorMatch[1].trim();
				const baseSelector = extractBaseSelector(fullSelector);

				// Only process class selectors (starting with .) or custom elements (ds-*)
				if (baseSelector.startsWith('.') || baseSelector.startsWith('ds-')) {
					// Extract the CSS rule block
					let braceCount = 0;
					let blockStart = trimmed.indexOf('{');
					let blockEnd = -1;
					let blockContent = '';
					let rawCSSStart = i;

					// Find the matching closing brace
					for (let j = i; j < lines.length; j++) {
						const currentLine = lines[j];
						for (let k = j === i ? blockStart : 0; k < currentLine.length; k++) {
							if (currentLine[k] === '{') {
								braceCount++;
							} else if (currentLine[k] === '}') {
								braceCount--;
								if (braceCount === 0) {
									blockEnd = j;
									// Get content from opening brace to closing brace
									if (j === i) {
										blockContent = currentLine.substring(blockStart + 1, k);
									} else {
										blockContent = trimmed.substring(blockStart + 1) + '\n';
										for (let l = i + 1; l < j; l++) {
											blockContent += lines[l] + '\n';
										}
										blockContent += currentLine.substring(0, k);
									}
									break;
								}
							}
						}
						if (blockEnd !== -1) break;
					}

					if (blockContent !== '') {
						const properties = parseProperties(blockContent);

						// Extract raw CSS block (for fullCSS generation)
						let rawCSS = '';
						if (blockEnd === i) {
							rawCSS = lines[i];
						} else {
							rawCSS = lines[i];
							for (let l = i + 1; l <= blockEnd; l++) {
								rawCSS += '\n' + lines[l];
							}
						}

						const pseudoClass = extractPseudoClass(fullSelector);

						const rule: ParsedRule = {
							fullSelector,
							properties,
							mediaQueryStack: [...mediaQueryStack], // Copy stack
							pseudoClass,
							rawCSS,
							lineStart: rawCSSStart,
							lineEnd: blockEnd,
							category: currentCategory,
							subcategory: currentSubcategory,
						};

						parsedRules.push(rule);

						// Skip to the end of this rule
						if (blockEnd !== -1) {
							i = blockEnd;
						}
					}
				}
			}
		}

		i++;
	}

	// Group rules by base selector
	const primitiveMap = new Map<string, ParsedPrimitiveData>();

	for (const rule of parsedRules) {
		const baseSelector = extractBaseSelector(rule.fullSelector);
		const isNested = isNestedSelector(rule.fullSelector);

		// Skip nested rules for now - we'll handle them separately
		if (isNested) {
			// Find the parent primitive (first part of nested selector)
			const parentBaseSelector = baseSelector;
			if (!primitiveMap.has(parentBaseSelector)) {
				primitiveMap.set(parentBaseSelector, {
					baseSelector: parentBaseSelector,
					category: 'other', // Will be set from non-nested rules
					allRules: [],
				});
			}
			// We'll attach nested rules later
			continue;
		}

		if (!primitiveMap.has(baseSelector)) {
			primitiveMap.set(baseSelector, {
				baseSelector,
				category: rule.category,
				subcategory: rule.subcategory,
				allRules: [],
			});
		}

		const primitiveData = primitiveMap.get(baseSelector)!;
		primitiveData.allRules.push(rule);

		// Set category and description from first base rule (no media query, no pseudo-class)
		if (!rule.mediaQueryStack.length && !rule.pseudoClass && !primitiveData.baseRule) {
			primitiveData.baseRule = rule;
			primitiveData.category = rule.category;
			primitiveData.subcategory = rule.subcategory;
		}
	}

	// Now handle nested rules - attach them to parent primitives
	for (const rule of parsedRules) {
		if (isNestedSelector(rule.fullSelector)) {
			const parentBaseSelector = extractBaseSelector(rule.fullSelector);
			if (primitiveMap.has(parentBaseSelector)) {
				primitiveMap.get(parentBaseSelector)!.allRules.push(rule);
			}
		}
	}

	// Convert to DesignSystemPrimitive[]
	const primitives: DesignSystemPrimitive[] = [];

	for (const [baseSelector, data] of primitiveMap.entries()) {
		// Separate rules into base, media queries, pseudo-classes, nested
		const baseRule = data.allRules.find(
			(r) => !r.mediaQueryStack.length && !r.pseudoClass && !isNestedSelector(r.fullSelector)
		);
		const mediaQueries: CSSMediaQuery[] = [];
		const pseudoClasses: CSSPseudo[] = [];
		const mediaQueryPseudos: CSSMediaQueryPseudo[] = [];
		const nestedRules: Array<CSSMediaQuery | CSSPseudo | CSSMediaQueryPseudo> = [];

		for (const rule of data.allRules) {
			const isNested = isNestedSelector(rule.fullSelector);
			const hasMedia = rule.mediaQueryStack.length > 0;
			const hasPseudo = !!rule.pseudoClass;

			if (isNested) {
				if (hasMedia && hasPseudo) {
					nestedRules.push({
						fullSelector: rule.fullSelector,
						properties: rule.properties,
						mediaQuery: rule.mediaQueryStack[rule.mediaQueryStack.length - 1],
						pseudoClass: rule.pseudoClass!,
					});
				} else if (hasMedia) {
					nestedRules.push({
						fullSelector: rule.fullSelector,
						properties: rule.properties,
						mediaQuery: rule.mediaQueryStack[rule.mediaQueryStack.length - 1],
					});
				} else if (hasPseudo) {
					nestedRules.push({
						fullSelector: rule.fullSelector,
						properties: rule.properties,
						pseudoClass: rule.pseudoClass!,
					});
				}
			} else if (hasMedia && hasPseudo) {
				mediaQueryPseudos.push({
					fullSelector: rule.fullSelector,
					properties: rule.properties,
					mediaQuery: rule.mediaQueryStack[rule.mediaQueryStack.length - 1],
					pseudoClass: rule.pseudoClass!,
				});
			} else if (hasMedia) {
				mediaQueries.push({
					fullSelector: rule.fullSelector,
					properties: rule.properties,
					mediaQuery: rule.mediaQueryStack[rule.mediaQueryStack.length - 1],
				});
			} else if (hasPseudo) {
				pseudoClasses.push({
					fullSelector: rule.fullSelector,
					properties: rule.properties,
					pseudoClass: rule.pseudoClass!,
				});
			}
		}

		// Generate fullCSS
		const fullCSS = generateFullCSS(data.allRules);

		// Get description from base rule
		const description = baseRule ? extractDescription(lines, baseRule.lineStart) : undefined;

		const primitive: DesignSystemPrimitive = {
			id: baseSelector.replace(/^\./, '').replace(/:/g, '-'),
			name: generateDisplayName(baseSelector),
			baseSelector,
			category: data.category,
			subcategory: data.subcategory,
			description,
			properties: baseRule?.properties || [],
			mediaQueries: mediaQueries.length > 0 ? mediaQueries : undefined,
			pseudoClasses: pseudoClasses.length > 0 ? pseudoClasses : undefined,
			mediaQueryPseudos: mediaQueryPseudos.length > 0 ? mediaQueryPseudos : undefined,
			nestedRules: nestedRules.length > 0 ? nestedRules : undefined,
			fullCSS,
		};

		primitives.push(primitive);
	}

	return primitives;
}
